import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { PageLayout } from "../components/page-layout";
import Loader from "../components/navigation/Loader";
import { getAllMessages } from "../services/message-service";
import "../styles/components/messaging.css";

const formatSentDate = (sentDateTime) => {
  if (!sentDateTime) return "—";

  const date = new Date(sentDateTime);
  if (Number.isNaN(date.getTime())) return "—";

  const datePart = date
    .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    .replace(",", "");
  const timePart = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  return `${datePart} ${timePart}`;
};

const formatMessageType = (messageType) => {
  const messageTypes = ["Email", "SMS", "Both"];
  return typeof messageType === "number" ? messageTypes[messageType] || "Unknown" : messageType || "Unknown";
};

const formatLanguage = (language) => {
  const languageNames = { en: "English", es: "Spanish", fr: "French" };
  const value = language || "en";

  return languageNames[value.toLowerCase()] || value;
};

const formatRecipientName = (recipient) => recipient.name || recipient.email || recipient.phoneNumber || "Unnamed recipient";

const getMessageSummary = (instances) => {
  const hasLanguage = (instance, language) => formatLanguage(instance.language).toLowerCase() === language;
  const instance = instances.find((item) => hasLanguage(item, "english"))
    || instances.find((item) => hasLanguage(item, "spanish"))
    || instances[0];

  return instance?.message || "—";
};

const formatDirection = (isInbound) => (isInbound === true ? "Received" : "Sent");

export const MessageHistoryPage = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expandedMessageIds, setExpandedMessageIds] = useState(new Set());
  const [recipientPopup, setRecipientPopup] = useState(null);

  const toggleMessage = (messageId) => {
    setExpandedMessageIds((currentIds) => {
      const updatedIds = new Set(currentIds);

      if (updatedIds.has(messageId)) {
        updatedIds.delete(messageId);
      } else {
        updatedIds.add(messageId);
      }

      return updatedIds;
    });
  };

  useEffect(() => {
    const loadMessages = async () => {
      try {
        const token = await getAccessTokenSilently();
        const result = await getAllMessages(token);

        if (result.error) {
          setError(true);
          return;
        }

        setMessages(result.data?.messages || []);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadMessages();
  }, [getAccessTokenSilently]);

  return (
    <PageLayout fullWidth>
      <section className="message-history">
        <div className="message-history__header">
          <div className="generic-title left">Messaging History</div>
          <button type="button" className="btn btn1 message-history__new-button" onClick={() => window.location.href = "/message/send"}>
            <i className="fa-solid fa-plus" aria-hidden="true" /> New Message
          </button>
        </div>
        {loading && <Loader />}
        {error && <p className="message-history__state">Unable to load messaging history.</p>}
        {!loading && !error && messages.length === 0 && (
          <p className="message-history__state">No messages have been sent yet.</p>
        )}
        {!loading && !error && messages.length > 0 && (
          <div className="message-history__table-wrapper">
            <table className="message-history__table">
              <thead>
                <tr>
                  <th scope="col">Message</th>
                  <th scope="col">Direction</th>
                  <th scope="col">Sent By</th>
                  <th scope="col">Sent</th>
                  <th scope="col">Details</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((message) => {
                  const instances = message.instances || [];
                  const isExpanded = expandedMessageIds.has(message.id);

                  return (
                    <React.Fragment key={message.id}>
                      <tr>
                        <td className="message-history__message">{getMessageSummary(instances)}</td>
                        <td>{formatDirection(message.isInbound)}</td>
                        <td>{message.sentByName || "—"}</td>
                        <td>{formatSentDate(message.sentDateTime)}</td>
                        <td>
                          <button
                            type="button"
                            className="message-history__expand-button"
                            aria-expanded={isExpanded}
                            onClick={() => toggleMessage(message.id)}
                          >
                            <i className={`fa-solid fa-chevron-${isExpanded ? "up" : "down"}`} aria-hidden="true" />
                            {isExpanded ? "Hide details" : "View details"}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="message-history__details-row">
                          <td colSpan="5">
                            {instances.length === 0 ? (
                              <p className="message-history__empty-instance">No delivery instances were recorded.</p>
                            ) : (
                              <table className="message-history__instances-table">
                                <thead>
                                  <tr>
                                    <th scope="col">Type</th>
                                    <th scope="col">Language</th>
                                    <th scope="col">Message</th>
                                    <th scope="col">Names</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {instances.map((instance, index) => (
                                    (() => {
                                      const recipients = instance.to || [];
                                      const recipientNames = recipients.map(formatRecipientName);

                                      return (
                                        <tr key={`${message.id}-${index}`}>
                                          <td>{formatMessageType(instance.messageType)}</td>
                                      <td>{formatLanguage(instance.language)}</td>
                                          <td className="message-history__message">{instance.message || "—"}</td>
                                          <td>
                                            {recipients.length > 1 ? (
                                              <button
                                                type="button"
                                                className="message-history__recipient-count"
                                                onClick={() => setRecipientPopup(recipientNames)}
                                              >
                                                {recipients.length} recipients
                                              </button>
                                            ) : recipientNames.join(", ") || "—"}
                                          </td>
                                        </tr>
                                      );
                                    })()
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {recipientPopup && (
        <div className="message-history__modal-backdrop" role="presentation" onMouseDown={() => setRecipientPopup(null)}>
          <section
            className="message-history__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="recipient-modal-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="message-history__modal-header">
              <h2 id="recipient-modal-title">Recipients</h2>
              <button type="button" className="message-history__modal-close" onClick={() => setRecipientPopup(null)}>
                Close
              </button>
            </div>
            <ul className="message-history__modal-list">
              {recipientPopup.map((name, index) => <li key={`${name}-${index}`}>{name}</li>)}
            </ul>
          </section>
        </div>
      )}
    </PageLayout>
  );
};
